<template>
  <div>
    <v-card-title class="ma-0 px-3 pb-3 pt-0">
      <p class="blue-grey--text text--darken-3 h6 ma-0 pa-0">
        {{ $t('routines_reminder.repetition_settings') }}
      </p>
    </v-card-title>

    <div class="px-4">
      <div class="d-flex mt-4">
        <v-text-field
          class="pt-1 mr-1 frequency-selector"
          data-testid="frequencySelectionStep-input-interval"
          hide-details
          dense
          outlined
          type="number"
          :label="$t('routines_reminder.every')"
          :min="1"
          :value="settings.interval"
          @input="$emit('change', 'interval', $event)"
        />

        <v-select
          class="pt-1 ml-2 frequency-type-selector"
          data-testid="frequencySelectionStep-select-frequency"
          dense
          hide-details
          item-text="text"
          outlined
          required
          :items="availableFrequencies"
          :label="$t('routines_reminder.period')"
          :menu-props="{ bottom: true, offsetY: true }"
          :value="settings.frequency"
          @input="handleRecurrenceChange"
        />
      </div>

      <div class="mt-4" v-if="settings.frequency === FREQUENCY_TYPE.MONTHLY">
        <DaySelector
          @change="handleDaySelector"
          :initialDay="calculatedInitialDay"
          :initialDate="formattedStartDate"
        />
      </div>

      <div
        v-if="settings.frequency === FREQUENCY_TYPE.WEEKLY"
        class="d-flex justify-space-between mt-4"
      >
        <div
          v-for="day in AVAILABLE_WEEKDAYS"
          class="week-day cursor-pointer d-flex justify-center align-center"
          :data-testid="`frequencySelectionStep-button-weekday-${day.value}`"
          :key="day.value"
          :class="{ selected: checkSelectedWeekday(day.value) }"
          @click="handleWeekdayClick(day.value)"
        >
          <span
            class="caption"
            :class="
              checkSelectedWeekday(day.value)
                ? 'white--text'
                : 'blue-grey--text text--darken-1'
            "
          >
            {{ day.text }}
          </span>
        </div>
      </div>

      <div v-if="showTopReminderHourSelector" class="mt-4">
        <HourSelector
          data-testid="frequencySelectionStep-select-reminderHour"
          :dataTestId="'frequencySelectionStep-select-reminderHour'"
          icon="mdi-clock-outline"
          :selectedHour="settings.reminderHour"
          @change="$emit('change', 'reminderHour', $event)"
        />
      </div>

      <div class="mt-4">
        <span
          class="subtitle2 font-weight-medium blue-grey--text text--darken-3"
        >
          {{ $t('routines_reminder.starts') }}
        </span>
      </div>

      <div class="mt-4">
        <MonthSelector
          v-if="settings.frequency === FREQUENCY_TYPE.MONTHLY"
          :initialMonth="monthSelected"
          @change="handleMonthSelector"
        />
        <DatePicker
          v-else
          editMode
          icon="mdi-calendar-outline"
          :dateFormat="dateFormat"
          :allowedDates="allowedDates"
          :dataTestid="'frequencySelectionStep-select-startDate'"
          :label="$t('common.date.date')"
          :value="settings.recurrenceStartDate"
          @change="$emit('change', 'recurrenceStartDate', $event)"
        />
        <HourSelector
          v-if="showInlineReminderHourSelector"
          class="mt-4"
          data-testid="frequencySelectionStep-select-reminderHour"
          :dataTestId="'frequencySelectionStep-select-reminderHour'"
          icon="mdi-clock-outline"
          :selectedHour="settings.reminderHour"
          @change="$emit('change', 'reminderHour', $event)"
        />
      </div>

      <div class="mt-4">
        <span
          class="subtitle2 font-weight-medium blue-grey--text text--darken-3"
        >
          {{ $t('routines_reminder.ends') }}
        </span>
      </div>

      <v-radio-group class="py-0 mt-2" hide-details v-model="selectedEndOption">
        <div class="d-flex align-center">
            <v-radio
              v-ripple="false"
              class="my-2"
              data-testid="frequencySelectionStep-radio-never"
              :value="END_VALUES.never"
              @click="handleNeverClick"
            >
          </v-radio>

          <p class="body1 blue-grey--text text--darken-3 ma-0 pa-0">
            {{ $t('routines_reminder.never') }}
          </p>
        </div>

        <div class="d-flex align-start justify-space-between">
          <div class="d-flex align-center">
            <v-radio
              v-ripple="false"
              class="my-2"
              data-testid="frequencySelectionStep-radio-on"
              :value="END_VALUES.on"
              @click="$emit('change', 'until', calculateEndDate())"
            >
            </v-radio>

            <p
              class="body1 blue-grey--text text--darken-3 ma-0 pa-0 radio-label"
            >
              {{ $t('routines_reminder.on') }}
            </p>
          </div>

          <div class="d-flex flex-column mt-3">
            <DatePicker
              editMode
              :allowedDates="allowedDates"
              :dataTestid="'frequencySelectionStep-select-until'"
              :disabled="selectedEndOption !== END_VALUES.on"
              :label="$t('routines_reminder.select_date')"
              :value="calculatedUntil"
              @change="$emit('change', 'until', $event)"
            />

            <HourSelector
              v-if="showHourlyUntilHourSelector"
              class="mt-4"
              data-testid="frequencySelectionStep-select-untilHour"
              icon="mdi-clock-outline"
              :dataTestId="'frequencySelectionStep-select-untilHour'"
              :disabled="selectedEndOption !== END_VALUES.on"
              :label="$t('routines_reminder.set_time')"
              :selectedHour="calculatedUntilHour"
              @change="handleUntilHourChange"
            />
          </div>
        </div>
      </v-radio-group>

      <Button
        plain
        class="mt-4"
        icon="mdi-repeat-off"
        iconPosition="left"
        :text="$t('routines_reminder.set_one_time')"
        @click="$emit('setOneTime')"
      />
    </div>
  </div>
</template>
<script>
import moment from 'moment'
import _ from 'lodash'
import Button from '@/components/UI/Button.vue'

import {
  ROUTINE_REMINDER_FREQUENCIES,
  AVAILABLE_WEEKDAYS,
  FREQUENCY_TYPE
} from '@/lib/frequencies'

import DatePicker from '@/components/Core/DatePicker.vue'
import HourSelector from './HourSelector.vue'
import DaySelector from './DaySelector.vue'
import MonthSelector from './MonthSelector.vue'

const END_VALUES = {
  never: 'never',
  on: 'on',
}

export default {
  name: 'FrequencySelectionStep',

  components: {
    DatePicker,
    HourSelector,
    DaySelector,
    MonthSelector,
    Button,
  },

  props: {
    settings: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      END_VALUES,
      AVAILABLE_WEEKDAYS,
      FREQUENCY_TYPE,
      selectedEndOption: this.settings?.until
        ? END_VALUES.on
        : END_VALUES.never,
      savedUntilDate: this.settings?.until,
      savedUntilHour: this.settings?.untilHour,
      monthSelected: moment(this.settings?.recurrenceStartDate).format('M'),
    }
  },

  computed: {
    dateFormat() {
      return this.settings?.frequency === FREQUENCY_TYPE.YEARLY
        ? 'MMMM D'
        : 'll'
    },

    formattedStartDate() {
      return moment(this.settings.recurrenceStartDate).format()
    },

    availableFrequencies() {
      return ROUTINE_REMINDER_FREQUENCIES.map(frequency => ({
        value: frequency.value,
        text:
          this.settings.interval > 1
            ? this.$t(frequency.plural_name)
            : this.$t(frequency.name),
      }))
    },

    selectedHour() {
      return moment('08:00', 'HH:mm').format('hh:mm A')
    },

    calculatedUntil() {
      return this.settings?.until
        ? this.settings?.until
        : this.calculateEndDate()
    },

    calculatedInitialDay() {
      if (_.isEmpty(this.settings?.byMonthDay)) {
        return moment(this.settings?.recurrenceStartDate).date()
      }
      if (this.settings?.byMonthDay?.length > 1) {
        return 'last_day'
      }
      return this.settings?.byMonthDay[0]
    },

    showHourlyUntilHourSelector() {
      return this.settings?.frequency === FREQUENCY_TYPE.HOURLY
    },

    showUntilHourSelector() {
      return (
        this.showHourlyUntilHourSelector &&
        this.selectedEndOption === END_VALUES.on
      )
    },

    showTopReminderHourSelector() {
      return this.settings?.frequency !== FREQUENCY_TYPE.HOURLY
    },

    showInlineReminderHourSelector() {
      return this.settings?.frequency === FREQUENCY_TYPE.HOURLY
    },

    calculatedUntilHour() {
      return (
        this.settings?.untilHour ||
        this.savedUntilHour ||
        this.settings?.reminderHour
      )
    },
  },

  watch: {
    'settings.untilHour'(newValue) {
      if (!newValue) return

      this.savedUntilHour = newValue
    },

    showUntilHourSelector: {
      immediate: true,
      handler(newValue) {
        if (!newValue || this.settings?.untilHour) return

        this.$emit('change', 'untilHour', this.calculatedUntilHour)
      },
    },
  },

  methods: {
    getDatePickerValue(date) {
      return date ? moment(date).format('YYYY-MM-DD') : null
    },

    allowedDates(date) {
      return date >= moment().format('YYYY-MM-DD')
    },

    handleDaySelector(day) {
      if (day === 'last_day') {
        return this.$emit('change', 'setLastDay', {
          bySetPosition: [-1],
          byMonthDay: [28, 29, 30, 31],
          recurrenceStartDate: moment({
            year: moment().year(),
            month: this.monthSelected - 1,
            day:
              moment(this.settings?.recurrenceStartDate)
                .endOf('month')
                .date() ?? null,
          }).format('YYYY-MM-DD'),
        })
      }
      // Ensure that bySetPosition is not -1
      this.$emit('change', 'bySetPosition', [])
      this.$emit('change', 'byMonthDay', [day])

      // Ensure that startDate is set
      this.$emit(
        'change',
        'recurrenceStartDate',
        moment({
          year: moment().year(),
          month: this.monthSelected - 1,
          day: day,
        }).format('YYYY-MM-DD')
      )
    },
    handleMonthSelector(month) {
      const dateWithCustomMonth = moment(this.settings?.recurrenceStartDate)
        .set('month', month - 1)
        .format('YYYY-MM-DD')
      if (this.settings?.byMonthDay.length > 1) {
        return this.$emit(
          'change',
          'recurrenceStartDate',
          moment(dateWithCustomMonth)
            .endOf('month')
            .format('YYYY-MM-DD')
        )
      }
      return dateWithCustomMonth
    },

    handleEmitStartDate() {
      this.$emit(
        'change',
        'recurrenceStartDate',
        moment({
          year: moment().year(),
          month: this.monthSelected - 1,
          day: this.calculatedInitialDay,
        }).format('YYYY-MM-DD')
      )
    },

    getWeekdayFirstLetter(weekday) {
      return weekday.substring(0, 1)
    },

    checkSelectedWeekday(weekday) {
      return this.settings?.byWeekDay?.includes(weekday)
    },

    handleWeekdayClick(selectedDay) {
      const localSelectedWeekdays = [...this.settings?.byWeekDay]
      if (localSelectedWeekdays?.includes(selectedDay)) {
        return this.$emit(
          'change',
          'byWeekDay',
          localSelectedWeekdays?.filter(day => day !== selectedDay)
        )
      }

      return this.$emit('change', 'byWeekDay', [
        ...localSelectedWeekdays,
        selectedDay,
      ])
    },

    handleRecurrenceChange(value) {
      if (this.settings?.frequency === FREQUENCY_TYPE.HOURLY) {
        this.savedUntilHour = this.calculatedUntilHour
      }

      if (value === FREQUENCY_TYPE.MONTHLY) {
        this.monthSelected = moment(this.settings?.recurrenceStartDate).format(
          'M'
        )
        this.$emit('change', 'byMonthDay', [
          moment(this.settings?.recurrenceStartDate).date(),
        ])
      } else {
        this.$emit('change', 'byMonthDay', [])
        this.$emit('change', 'bySetPosition', [])
      }

      if (value === FREQUENCY_TYPE.WEEKLY) {
        this.$emit('change', 'byWeekDay', ['MO'])
      } else {
        this.$emit('change', 'byWeekDay', [])
      }

      return this.$emit('change', 'frequency', value)
    },

    handleUntilHourChange(value) {
      this.savedUntilHour = value
      this.$emit('change', 'untilHour', value)
    },

    calculateEndDate() {
      if (this.savedUntilDate) return this.savedUntilDate
      const dateObject = moment(this.settings.recurrenceStartDate)
      const monthlyDate = moment({
        year: moment().year(),
        month: this.monthSelected - 1,
        day: this.calculatedInitialDay,
      })
      if (this.calculatedInitialDay === 'last_day') {
        const lastDay = dateObject
          .add(1, 'years')
          .endOf('month')
          .format('YYYY-MM-DD')

        return lastDay
      }
      switch (this.settings.frequency) {
        case FREQUENCY_TYPE.HOURLY:
          return dateObject.add(1, 'days').format('YYYY-MM-DD')
        case FREQUENCY_TYPE.DAILY:
          return dateObject.add(1, 'months').format('YYYY-MM-DD')
        case FREQUENCY_TYPE.WEEKLY:
          return dateObject.add(3, 'months').format('YYYY-MM-DD')
        case FREQUENCY_TYPE.MONTHLY:
          return monthlyDate.add(1, 'years').format('YYYY-MM-DD')
        case FREQUENCY_TYPE.YEARLY:
          return dateObject.add(5, 'years').format('YYYY-MM-DD')
        default:
          return dateObject
      }
    },

    handleNeverClick() {
      this.savedUntilDate = this.settings.until
      this.$emit('change', 'until', null)
    },
  },
}
</script>
<style lang="scss" scoped>
.frequency-selector {
  width: 140px;
}

.frequency-type-selector {
  max-width: 100%;
}

.radio-label {
  width: 60px !important;
}

.week-day {
  border-radius: 50%;
  width: 28px;
  height: 28px;
  background: $color-blue-grey-lighten3;
}

.selected {
  background: $color-blue;
}
</style>
